import { ref, computed, watch } from 'vue';
import { TransactionType } from '@/common/config';
import { transactionParamsSwitch } from '@/common/sb-transaction.helpers';
import type { TransStatement } from '@/common/api/model';
import type { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import { usePaymentTransactions as usePaymentTransactionsQuery } from '../api/paymentTransactions.api';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow/useMockedFlowStore';
import { createMockedTransactions } from '@/library/cf-v1/mocked-flow';

export interface UsePaymentTransactionsOptions {
  transactionSubtypes: ExternalTransactionKind[];
  pageSize?: number;
}

/**
 * Build comma-separated transaction status codes from transaction subtypes
 */
function buildTransactionStatusCodes(transactionSubtypes: ExternalTransactionKind[]): string {
  return transactionSubtypes
    .map((transactionType) => {
      const result = transactionParamsSwitch.match(transactionType);
      return result?.status;
    })
    .filter((status): status is number => status !== undefined)
    .join(',');
}

/**
 * Composable for managing payment transactions (deposits/withdrawals)
 * Provides pagination, loading states, and data transformation
 * Returns mocked data when in mocked flow mode
 */
export function usePaymentTransactions(options: UsePaymentTransactionsOptions) {
  const { transactionSubtypes, pageSize } = options;
  const mockedFlowStore = useMockedFlowStore();

  // Check if we're in mocked flow mode
  const isMockedFlow = computed(() => mockedFlowStore.isActive);

  // Derive transaction type and status codes from subtypes
  const firstSubtype = computed(() => transactionSubtypes[0]);
  const transactionParams = computed(() => {
    if (!firstSubtype.value) return null;
    return transactionParamsSwitch.match(firstSubtype.value);
  });

  const transactionType = computed(
    () => transactionParams.value?.type as TransactionType.Deposit | TransactionType.Withdrawal | undefined
  );
  const statusCodes = computed(() => buildTransactionStatusCodes(transactionSubtypes));

  const currentPage = ref(1);
  /** Accumulates all loaded pages so "load more" shows a full list instead of only the latest page */
  const accumulatedStatements = ref<TransStatement[]>([]);

  // Query parameters for TanStack Query
  const queryParams = computed(() => ({
    transactionType: transactionType.value!,
    pageSize,
    pageNo: currentPage.value,
    statuses: statusCodes.value,
  }));

  // Fetch data using TanStack Query (disabled when in mocked flow)
  const { data, isLoading, error, refetch } = usePaymentTransactionsQuery(queryParams, {
    enabled: computed(() => !isMockedFlow.value),
  });

  // Mocked transactions data
  const mockedTransactions = computed(() => (isMockedFlow.value ? createMockedTransactions() : []));

  // Keep accumulated list in sync with fetched pages (so load more shows full list)
  watch(
    [() => data.value, currentPage],
    ([response]) => {
      if (isMockedFlow.value) return;
      const statements = (response as { statements?: TransStatement[] } | undefined)?.statements ?? [];
      if (statements.length === 0) return;
      if (currentPage.value === 1) {
        accumulatedStatements.value = [...statements];
      } else {
        const existingIds = new Set(accumulatedStatements.value.map((s) => s.tradeId));
        const newStatements = statements.filter((s) => !existingIds.has(s.tradeId));
        if (newStatements.length > 0) {
          accumulatedStatements.value = [...accumulatedStatements.value, ...newStatements];
        }
      }
    },
    { immediate: true }
  );

  // Computed properties - return full TransStatement objects (accumulated across pages when not mocked)
  const transactions = computed<TransStatement[]>(() =>
    isMockedFlow.value ? mockedTransactions.value : accumulatedStatements.value
  );
  const totalTransactions = computed(() =>
    isMockedFlow.value ? mockedTransactions.value.length : data.value?.totalNum || 0
  );
  const totalPages = computed(() =>
    isMockedFlow.value ? 1 : Math.ceil((data.value?.totalNum || 1) / (pageSize || 10))
  );
  const hasMore = computed(() => (isMockedFlow.value ? true : currentPage.value < totalPages.value));
  const loading = computed(() => (isMockedFlow.value ? false : isLoading.value));

  // Actions
  const loadMore = () => {
    if (isMockedFlow.value || !hasMore.value || isLoading.value) return;
    currentPage.value += 1;
  };

  const refresh = async () => {
    if (isMockedFlow.value) return;
    currentPage.value = 1;
    accumulatedStatements.value = [];
    await refetch();
  };

  const goToPage = (page: number) => {
    if (isMockedFlow.value) return;
    if (page < 1 || page > totalPages.value) return;
    currentPage.value = page;
  };

  return {
    // Full TransStatement objects (not transformed)
    transactions,

    // State
    loading,
    error: computed(() => (isMockedFlow.value ? null : error.value?.message || null)),
    currentPage,
    totalPages,
    totalTransactions,
    hasMore,

    // Actions
    loadMore,
    refresh,
    goToPage,
  };
}
